import { useCallback } from 'react';
import type { SaveWorkspaceRequest } from '../../lib/api/workspaces/dto';
import {
  useCreateNewWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useDuplicateWorkspaceMutation,
  useRenameWorkspaceMutation,
  useSaveWorkspaceMutation,
} from '../api/useWorkspaces';

export const useWorkspaceApi = () => {
  const { trigger: createNewWorkspace, ...newWorkspaceResponse } = useCreateNewWorkspaceMutation();
  const { trigger: deleteWorkspace, ...deleteResponse } = useDeleteWorkspaceMutation();
  const { trigger: runSaveWorkspace, ...saveResponse } = useSaveWorkspaceMutation();
  const { trigger: duplicateWorkspace, ...duplicateWorkspaceResponse } = useDuplicateWorkspaceMutation();

  const { trigger: renameWorkspace, ...renameWorkspaceResponse } = useRenameWorkspaceMutation();

  const saveWorkspace = useCallback(
    async (newWorkspace: SaveWorkspaceRequest) => {
      const { workspaceID, workspaceName, accountID, updatedBy, advertiserID, lookerFilters, userID, details } =
        newWorkspace;

      if (!workspaceID || !workspaceName) {
        console.error('Missing parameters for saving workspace', {
          workspaceID,
          workspaceName,
          updatedBy,
          accountID,
        });

        return null;
      }

      try {
        let newWorkspaceObject: SaveWorkspaceRequest = {
          workspaceID,
          workspaceName,
          workspaceDescription: newWorkspace.workspaceDescription,
          workspaceStatusID: newWorkspace.workspaceStatusID,
          accountID,
          userID,
          updatedBy: newWorkspace.updatedBy?.toString(),
          advertiserID: advertiserID,
          lookerFilters,
          details: details ?? undefined,
          createList: newWorkspace.createList ?? false,
          createSchedule: newWorkspace.createSchedule ?? false,
          isDraft: newWorkspace.isDraft,
          ownedAndOperatedUsed: newWorkspace.ownedAndOperatedUsed,
          statusConfig: newWorkspace.statusConfig,
        };

        if (newWorkspace.createList || newWorkspace.createSchedule) {
          newWorkspaceObject = {
            ...newWorkspaceObject,
            applications: newWorkspace.applications || [''],
            timezone: newWorkspace.timezone || '',
            cron: newWorkspace.cron || '',
          };
        }

        return runSaveWorkspace(newWorkspaceObject);
      } catch (error) {
        console.error('Error saving workspace:', error);
        return null;
      }
    },
    [runSaveWorkspace],
  );

  return {
    createNewWorkspace,
    deleteWorkspace,
    duplicateWorkspace,
    saveWorkspace,
    renameWorkspace,

    newWorkspaceResponse,
    newWorkspaceData: newWorkspaceResponse.data,

    deleteResponse,
    duplicateWorkspaceResponse,
    saveResponse,
    renameResponse: renameWorkspaceResponse,

    wasNewWorkspaceCreatedSuccessfully: newWorkspaceResponse.isSuccess,
    isCreateNewWorkspaceLoading: newWorkspaceResponse.isLoading,

    wasDeleteSuccessful: deleteResponse.isSuccess,
    isDeleteLoading: deleteResponse.isLoading,
    isDeleteError: deleteResponse.isError,

    wasDuplicateSuccessful: duplicateWorkspaceResponse.isSuccess,
    isDuplicateLoading: duplicateWorkspaceResponse.isLoading,

    wasSaveSuccessful: saveResponse.isSuccess,
    isSaveLoading: saveResponse.isLoading,
  };
};
